import { useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { yupDateTransformer } from '../../../../utilities/dayjs-utilities';
import { DATE_FORMAT } from '../../../../utilities/constants';
import Validation from '../../../../constants/validation-messages';
import Regex from '../../../../constants/regex';
import { trackCustomEvent } from '../../../../utilities/app-insights';

/**
 * Ampol Cash Filters Hook
 *
 * Manages filter, search, sorting, and pagination state for Ampol Cash Orders.
 *
 * @param loggedInUser - User context for tracking
 * @param assignedAccountNumber - Account number for tracking
 * @returns Filter state and handlers
 */
export const useAmpolCashFilters = (loggedInUser: any, assignedAccountNumber: string) => {
  // Returns fresh dates every time it is called — avoids frozen values from mount-time useMemo
  const getDefaultFilterValues = useCallback(
    () => ({
      dateFrom: dayjs().subtract(3, 'month').format(DATE_FORMAT),
      dateTo: dayjs().format(DATE_FORMAT),
      orderNumber: '',
      orderValueFrom: null,
      orderValueTo: null,
      orderStatus: null,
    }),
    []
  );

  const defaultFilterValues = getDefaultFilterValues();

  const defaultSearchValues = useMemo(
    () => ({
      cardNumber: '',
      orderNumber: '',
    }),
    []
  );

  // State
  const [sortColumn, setSortColumn] = useState('orderDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterBarEnabled, setFilterBarEnabled] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [cashOrdersQueryParams, setCashOrdersQueryParams] = useState({
    filterValues: {
      ...defaultFilterValues,
      pageNumber: 1,
    },
    searchValues: { ...defaultSearchValues },
    changedFromOnClear: false,
  });

  // Validation functions
  const validateMaxTwoDecimals = useCallback((val: any) => {
    return val ? Regex.NUM_WITH_OPTIONAL_DECIMAL.test(val) : true;
  }, []);

  // Filter form schema (memoized for performance)
  const filterFormSchema = useMemo(
    () =>
      yup.object().shape({
        dateFrom: yup.date().nullable().required(Validation.CASH_DATE_FROM_REQUIRED).transform(yupDateTransformer),
        dateTo: yup
          .date()
          .nullable()
          .required(Validation.CASH_DATE_TO_REQUIRED)
          .transform(yupDateTransformer)
          .min(yup.ref('dateFrom'), Validation.CASH_DATE_TO_AFTER_DATE_FROM),
        orderNumber: yup.string().nullable().matches(Regex.ALPHA_NUM, Validation.ALPHA_NUM),
        orderValueFrom: yup
          .number()
          .typeError(Validation.ORDER_VALUE)
          .test('is-valid-decimal', Validation.ORDER_VALUE, validateMaxTwoDecimals)
          .min(0, Validation.ORDER_VALUE_MIN)
          .max(999999, Validation.ORDER_VALUE_MAX)
          .nullable()
          .transform((_, val) => (val ? Number(val) : null)),
        orderValueTo: yup
          .number()
          .typeError(Validation.ORDER_VALUE)
          .test('is-valid-decimal', Validation.ORDER_VALUE, validateMaxTwoDecimals)
          .min(0, Validation.ORDER_VALUE_MIN)
          .max(999999, Validation.ORDER_VALUE_MAX)
          .when(['orderValueFrom'], (from: any, schema: any) => {
            return !from ? schema : schema.min(from, Validation.ORDER_VALUE_TO_GREATER_FROM);
          })
          .nullable()
          .transform((_, val) => (val ? Number(val) : null)),
      }),
    [validateMaxTwoDecimals]
  );

  // Search validation schema
  const searchSchema = useCallback((searchTerm: string) => {
    const validate =
      Regex.CARD_NUMBER.test(searchTerm) ||
      Regex.SECURE_CARD_NUMBER.test(searchTerm) ||
      Regex.CASH_ORDER_NUMBER.test(searchTerm);
    if (validate) {
      return '';
    } else {
      return Validation.VALID_CARD_OR_ORDER;
    }
  }, []);

  // Pagination helper
  const pageSize = 10;
  const offset = (cashOrdersQueryParams.filterValues.pageNumber - 1) * pageSize;

  // Handlers (all memoized with useCallback)
  const handleTableColumnSorted = useCallback((column: string, isAscending: boolean) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  }, []);

  const handlePageNumberChange = useCallback(
    (value: number) => {
      trackCustomEvent('Change Page Number', loggedInUser, assignedAccountNumber, 'Changed page number.');
      setCashOrdersQueryParams((prev) => ({
        ...prev,
        filterValues: {
          ...prev.filterValues,
          pageNumber: value,
        },
        changedFromOnClear: false,
      }));
    },
    [loggedInUser, assignedAccountNumber]
  );

  const onToggleFilter = useCallback(() => {
    setFilterBarEnabled((prev) => !prev);
  }, []);

  const handleFilterSubmit = useCallback(
    (values: any) => {
      setSearchTerm('');
      // eslint-disable-next-line no-unused-vars
      const { pageNumber, ...valuesWithoutPage } = values;

      const formValues = {
        ...valuesWithoutPage,
        dateFrom: values.dateFrom ? dayjs(values.dateFrom).format(DATE_FORMAT) : null,
        dateTo: values.dateTo ? dayjs(values.dateTo).format(DATE_FORMAT) : null,
      };

      const isDefaultValues =
        Object.keys(formValues).length === Object.keys(defaultFilterValues).length &&
        Object.keys(formValues).every(
          (key) =>
            formValues[key as keyof typeof formValues] === defaultFilterValues[key as keyof typeof defaultFilterValues]
        );

      setCashOrdersQueryParams({
        filterValues: { ...formValues, pageNumber: 1 },
        searchValues: { ...defaultSearchValues },
        changedFromOnClear: false,
      });
      setFilterBarEnabled(false);
      setFilterApplied(!isDefaultValues);

      trackCustomEvent(
        'Filter Cash',
        loggedInUser,
        assignedAccountNumber,
        'Filtered list of orders by date range, order value or status.'
      );
    },
    [loggedInUser, assignedAccountNumber, defaultFilterValues, defaultSearchValues]
  );

  const handleClearFilters = useCallback(() => {
    window.dataLayer.push({
      event: 'Card_Portal_All_Clear_All',
      button_click: true,
    });

    const freshDefaults = getDefaultFilterValues();
    setCashOrdersQueryParams((prev) => ({
      ...prev,
      filterValues: {
        ...freshDefaults,
        dateFrom: null,
        dateTo: null,
        pageNumber: prev.filterValues.pageNumber,
      },
      changedFromOnClear: true,
    }));
  }, [getDefaultFilterValues]);

  // Resets filter state without firing a GTM event — used by handleSearch
  const resetFilterState = useCallback(() => {
    const freshDefaults = getDefaultFilterValues();
    setCashOrdersQueryParams((prev) => ({
      ...prev,
      filterValues: {
        ...freshDefaults,
        dateFrom: null,
        dateTo: null,
        pageNumber: prev.filterValues.pageNumber,
      },
      changedFromOnClear: true,
    }));
  }, [getDefaultFilterValues]);

  const handleFilterChange = useCallback((values: any) => {
    setCashOrdersQueryParams((prev) => ({
      ...prev,
      filterValues: {
        ...prev.filterValues,
        ...values,
      },
      changedFromOnClear: true,
    }));
  }, []);

  const handleSearch = useCallback(
    (searchTerm: string) => {
      resetFilterState(); // reset without GTM
      setFilterBarEnabled(false);
      setFilterApplied(false);

      const freshDefaults = getDefaultFilterValues();

      if (searchTerm.match(Regex.CARD_NUMBER) || searchTerm.match(Regex.SECURE_CARD_NUMBER)) {
        setCashOrdersQueryParams({
          filterValues: {
            ...freshDefaults,
            pageNumber: 1,
          },
          searchValues: {
            cardNumber: searchTerm,
            orderNumber: '',
          },
          changedFromOnClear: false,
        });
      } else if (searchTerm.match(Regex.CASH_ORDER_NUMBER)) {
        setCashOrdersQueryParams({
          filterValues: {
            ...freshDefaults,
            pageNumber: 1,
          },
          searchValues: {
            cardNumber: '',
            orderNumber: searchTerm,
          },
          changedFromOnClear: false,
        });
      } else {
        setCashOrdersQueryParams({
          filterValues: {
            ...freshDefaults,
            pageNumber: 1,
          },
          searchValues: { ...defaultSearchValues },
          changedFromOnClear: false,
        });
      }

      setSearchTerm(searchTerm);
    },
    [getDefaultFilterValues, defaultSearchValues, resetFilterState]
  );

  const resetFiltersOnAccountChange = useCallback(() => {
    const freshDefaults = getDefaultFilterValues();
    setCashOrdersQueryParams({
      filterValues: {
        ...freshDefaults,
        pageNumber: 1,
      },
      searchValues: { ...defaultSearchValues },
      changedFromOnClear: false,
    });
    setFilterApplied(false);
    setSearchTerm('');
  }, [getDefaultFilterValues, defaultSearchValues]);

  const updateQueryParams = useCallback((updates: Partial<typeof cashOrdersQueryParams>) => {
    setCashOrdersQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Return stable API
  return {
    // State
    sortColumn,
    sortDirection,
    filterBarEnabled,
    filterApplied,
    searchTerm,
    cashOrdersQueryParams,
    defaultFilterValues,
    defaultSearchValues,

    // Computed values
    pageSize,
    offset,
    filterFormSchema,
    searchSchema,

    // Handlers
    setSortColumn,
    setSortDirection,
    setSearchTerm,
    handleTableColumnSorted,
    handlePageNumberChange,
    onToggleFilter,
    handleFilterSubmit,
    handleClearFilters,
    handleFilterChange,
    handleSearch,
    resetFiltersOnAccountChange,
    updateQueryParams,
  };
};
